/**
 * Playlist API Service
 * Handles all API calls for user playlists (learner-side course collections)
 */

import { apiClient as api } from './apiClient';
import type { Course } from './coursesService';

import { API_BASE_URL } from '../config';
const PLAYLISTS_API_BASE = `${API_BASE_URL}/api/v1/playlists`;
const COURSES_API_BASE = `${API_BASE_URL}/api/v1/courses`;

// Types for Playlist entities
export interface PlaylistCourse {
  id: string;
  playlistId: string;
  courseId: string;
  course: Course;
  orderIndex: number;
  addedAt: string;
}

export interface Playlist {
  id: string;
  userId: string;
  title: string;
  description?: string;
  isPublic: boolean;
  playlistCourses?: PlaylistCourse[];
  user?: {
    id: string;
    fullName?: string;
    username?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface PlaylistWithStatus extends Playlist {
  hasCourse: boolean;
}

export interface CreatePlaylistDto {
  title: string;
  description?: string;
  isPublic?: boolean;
}

export interface UpdatePlaylistDto {
  title?: string;
  description?: string;
  isPublic?: boolean;
}

export interface AddCourseToPlaylistDto {
  playlistId: string;
}

export interface AddCourseWithOrderDto {
  courseId: string;
  order?: number;
}

// API Functions
export const playlistService = {
  /**
   * Create a new playlist
   */
  async create(data: CreatePlaylistDto): Promise<Playlist> {
    const response = await api.post<Playlist>(PLAYLISTS_API_BASE, data);
    return response.data;
  },

  /**
   * Get current user's playlists
   */
  async getMyPlaylists(): Promise<Playlist[]> {
    const response = await api.get<Playlist[]>(`${PLAYLISTS_API_BASE}/me`);
    return response.data;
  },

  /**
   * Get playlists with status for a specific course
   * Returns whether each playlist contains the given course
   */
  async getPlaylistsWithCourseStatus(courseId: string): Promise<PlaylistWithStatus[]> {
    const response = await api.get<PlaylistWithStatus[]>(
      `${PLAYLISTS_API_BASE}/me/with-course-status`,
      { params: { courseId } }
    );
    return response.data;
  },

  /**
   * Get a single playlist by ID
   */
  async getById(id: string): Promise<Playlist> {
    const response = await api.get<Playlist>(`${PLAYLISTS_API_BASE}/${id}`);
    return response.data;
  },

  /**
   * Update a playlist
   */
  async update(id: string, data: UpdatePlaylistDto): Promise<Playlist> {
    const response = await api.patch<Playlist>(`${PLAYLISTS_API_BASE}/${id}`, data);
    return response.data;
  },

  /**
   * Delete a playlist
   */
  async delete(id: string): Promise<void> {
    await api.delete(`${PLAYLISTS_API_BASE}/${id}`);
  },

  /**
   * Add a course to a playlist
   */
  async addCourse(playlistId: string, data: AddCourseWithOrderDto): Promise<Playlist> {
    const response = await api.post<Playlist>(
      `${PLAYLISTS_API_BASE}/${playlistId}/courses`,
      data
    );
    return response.data;
  },

  /**
   * Remove a course from a playlist
   */
  async removeCourse(playlistId: string, courseId: string): Promise<Playlist> {
    const response = await api.delete<Playlist>(
      `${PLAYLISTS_API_BASE}/${playlistId}/courses/${courseId}`
    );
    return response.data;
  },

  /**
   * Quick add course to playlist (from course card)
   */
  async addCourseQuick(
    courseId: string,
    playlistId: string
  ): Promise<{ success: boolean; playlist: Playlist }> {
    const response = await api.post<{ success: boolean; playlist: Playlist }>(
      `${COURSES_API_BASE}/${courseId}/add-to-playlist`,
      { playlistId }
    );
    return response.data;
  },
};

export default playlistService;
